<template>
  <div class="field-properties-panel">
    <template v-if="field">
      <div class="text-subtitle2 q-mb-md">Field Properties</div>

      <!-- Type (read-only) -->
      <div class="prop-row q-mb-sm">
        <div class="text-caption text-grey-7">Type</div>
        <div class="text-body2">
          <q-icon :name="fieldDef.icon" size="16px" class="q-mr-xs" />
          {{ fieldDef.label }}
        </div>
      </div>

      <!-- Note: Label text content -->
      <q-input
        v-if="field && field.type === 'Note'"
        :model-value="field.defaultValue || ''"
        label="Label Text"
        dense
        outlined
        class="q-mb-sm"
        @update:model-value="val => emitUpdate({ defaultValue: val || '' })"
      />

      <!-- Signer assignment (not for Note) -->
      <q-select
        v-if="field && field.type !== 'Note'"
        :model-value="field.signerIndex"
        :options="signerOptions"
        label="Assigned to"
        dense
        outlined
        emit-value
        map-options
        class="q-mb-sm"
        @update:model-value="val => emitUpdate({ signerIndex: val })"
      />

      <!-- Label (not for Note - uses Label Text instead) -->
      <q-input
        v-if="field && field.type !== 'Note'"
        :model-value="field.label || ''"
        label="Custom Label"
        dense
        outlined
        clearable
        class="q-mb-sm"
        @update:model-value="val => emitUpdate({ label: val || null })"
      />

      <!-- Required toggle (not for Note) -->
      <q-toggle
        v-if="field && field.type !== 'Note'"
        :model-value="field.required"
        label="Required"
        class="q-mb-md"
        @update:model-value="val => emitUpdate({ required: val })"
      />

      <!-- Must be checked toggle (checkbox-only, when required) -->
      <q-toggle
        v-if="field && field.type === 'Checkbox' && field.required"
        :model-value="field.mustBeChecked !== false"
        label="Must be checked"
        class="q-mb-md"
        @update:model-value="val => emitUpdate({ mustBeChecked: val })"
      >
        <q-tooltip>When enabled, the signer must check this box to complete signing. When disabled, the signer only needs to interact with it.</q-tooltip>
      </q-toggle>

      <!-- Dropdown options editor -->
      <template v-if="field && (field.type === 'Dropdown' || field.type === 'RadioGroup')">
        <div class="text-caption text-grey-7 q-mb-xs">Options</div>
        <div v-for="(opt, i) in (field.options || [])" :key="i" class="row items-center no-wrap q-mb-xs">
          <div class="col text-body2 ellipsis">{{ opt }}</div>
          <q-btn flat round dense size="xs" icon="close" color="negative" @click="removeOption(i)" />
        </div>
        <div class="row items-center no-wrap q-mb-md">
          <q-input
            v-model="newOption"
            dense
            outlined
            placeholder="Add option..."
            class="col"
            @keyup.enter="addOption"
          />
          <q-btn flat round dense icon="add" color="primary" class="q-ml-xs" :disable="!newOption" @click="addOption" />
        </div>
      </template>

      <q-separator class="q-mb-md" />

      <!-- Position info (read-only) -->
      <div class="prop-row q-mb-xs">
        <div class="text-caption text-grey-7">Position</div>
        <div class="text-caption">
          Page {{ field.pageNumber }}, X: {{ Math.round(field.x) }}, Y: {{ Math.round(field.y) }}
        </div>
      </div>
      <div class="prop-row q-mb-md">
        <div class="text-caption text-grey-7">Size</div>
        <div class="text-caption">
          {{ Math.round(field.widthPt) }} x {{ Math.round(field.heightPt) }} pt
        </div>
      </div>

      <q-separator class="q-mb-md" />

      <!-- Delete -->
      <q-btn
        color="negative"
        outline
        icon="delete"
        label="Delete Field"
        no-caps
        class="full-width"
        @click="$emit('delete', field.id)"
      />
    </template>

    <template v-else>
      <div class="text-center text-grey-6 q-pa-lg">
        <q-icon name="touch_app" size="48px" class="q-mb-sm" />
        <div class="text-body2">Select a field to edit its properties</div>
      </div>
    </template>
  </div>
</template>

<script>
import { FIELD_TYPES } from '../pdf/fieldConstants.js';

export default {
  name: 'FieldPropertiesPanel',
  props: {
    field: { type: Object, default: null },
    signers: { type: Array, default: () => [] },
  },
  emits: ['update', 'delete'],
  data() {
    return { newOption: '' };
  },
  watch: {
    field: {
      handler(f) {
        if (f && f.type === 'Dropdown') {
          console.log('[PropertiesPanel] field changed, type=Dropdown, options=', JSON.stringify(f.options));
        }
      },
      deep: true,
      immediate: true,
    },
  },
  computed: {
    fieldDef() {
      if (!this.field) return FIELD_TYPES.Text;
      return FIELD_TYPES[this.field.type] || FIELD_TYPES.Text;
    },
    signerOptions() {
      return this.signers.map((s, i) => ({
        value: i,
        label: s.name || s.email || `Signer ${i + 1}`,
      }));
    },
  },
  methods: {
    emitUpdate(changes) {
      if (!this.field) return;
      this.$emit('update', { id: this.field.id, ...changes });
    },
    addOption() {
      if (!this.newOption || !this.field) return;
      const options = [...(this.field.options || []), this.newOption.trim()];
      this.newOption = '';
      console.log('[PropertiesPanel.addOption] emitting options=', JSON.stringify(options));
      this.emitUpdate({ options });
    },
    removeOption(index) {
      if (!this.field) return;
      const options = [...(this.field.options || [])];
      options.splice(index, 1);
      this.emitUpdate({ options });
    },
  },
};
</script>

<style scoped>
.field-properties-panel {
  width: 240px;
  min-width: 240px;
  padding: 12px;
  overflow-y: auto;
  border-left: 1px solid #e0e0e0;
  background: #fafafa;
}

.prop-row {
  display: flex;
  flex-direction: column;
}
</style>
